import React, { useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { SearchIcon, FilterIcon, MapPinIcon, CheckCircleIcon, GridIcon, ListIcon, MapIcon, StarIcon, ChevronRightIcon } from 'lucide-react';
import { developers } from '../utils/data';
import { Developer } from '../utils/types';
import Pagination from '../components/ui/Pagination';
import { TalentCapsuleCard } from '../components/developers/TalentCapsuleCard';
type ViewMode = 'grid' | 'spotlight' | 'collections' | 'map';
const Developers = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [filters, setFilters] = useState({
    status: '',
    role: '',
    position: '',
    country: '',
    skills: [] as string[]
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const developersPerPage = 8;
  // Certification exams data with ALL required properties
  const certificationExams = [{
    id: '1',
    name: 'Laravel Advanced Certification',
    description: 'Master advanced Laravel concepts including queues, events, broadcasting, and advanced Eloquent techniques.',
    price: 149,
    currency: 'USD',
    duration: '180',
    questions: 50,
    technologies: ['Laravel', 'PHP', 'MySQL'],
    topics: ['Advanced Eloquent', 'Queue Management', 'Event Broadcasting', 'API Development', 'Testing'],
    level: 'Advanced',
    badge: 'https://via.placeholder.com/80?text=Laravel'
  }, {
    id: '2',
    name: 'Vue.js Professional Certification',
    description: 'Comprehensive Vue.js certification covering Composition API, Vuex, Vue Router, and best practices.',
    price: 129,
    currency: 'USD',
    duration: '150',
    questions: 45,
    technologies: ['Vue.js', 'JavaScript', 'Vuex'],
    topics: ['Composition API', 'State Management', 'Vue Router', 'Component Design', 'Performance'],
    level: 'Professional',
    badge: 'https://via.placeholder.com/80?text=Vue'
  }, {
    id: '3',
    name: 'Full-Stack Laravel Developer',
    description: 'Complete full-stack certification combining Laravel backend with modern frontend frameworks.',
    price: 199,
    currency: 'USD',
    duration: '240',
    questions: 60,
    technologies: ['Laravel', 'Vue.js', 'React', 'API Development'],
    topics: ['Backend Architecture', 'Frontend Integration', 'API Design', 'Authentication', 'Deployment'],
    level: 'Expert',
    badge: 'https://via.placeholder.com/80?text=Full-Stack'
  }, {
    id: '4',
    name: 'React & Laravel Integration',
    description: 'Learn to build modern SPAs with React frontend and Laravel API backend.',
    price: 139,
    currency: 'USD',
    duration: '180',
    questions: 40,
    technologies: ['React', 'Laravel', 'REST APIs'],
    topics: ['React Fundamentals', 'Laravel API', 'State Management', 'Authentication'],
    level: 'Intermediate',
    badge: 'https://via.placeholder.com/80?text=React'
  }];
  // Quick filter chips
  const quickFilters = [{
    label: 'Laravel',
    type: 'skill',
    value: 'Laravel'
  }, {
    label: 'Vue.js',
    type: 'skill',
    value: 'Vue.js'
  }, {
    label: 'React',
    type: 'skill',
    value: 'React'
  }, {
    label: 'Full-stack',
    type: 'role',
    value: 'Full-stack Developer'
  }, {
    label: 'Available now',
    type: 'status',
    value: 'available'
  }, {
    label: '<€75/hr',
    type: 'rate',
    value: '75'
  }];
  // Get unique values for filters
  const countries = [...new Set(developers.map(dev => dev.country))];
  const roles = [...new Set(developers.map(dev => dev.role))];
  const positions = [...new Set(developers.map(dev => dev.position))];
  const statuses = ['available', 'unavailable', 'looking'];
  const allSkills = [...new Set(developers.flatMap(dev => dev.skills.map(s => s.name)))];
  // Filter developers
  const filteredDevelopers = developers.filter(dev => {
    const matchesSearch = searchTerm === '' || dev.name.toLowerCase().includes(searchTerm.toLowerCase()) || dev.title.toLowerCase().includes(searchTerm.toLowerCase()) || dev.skills.some(skill => skill.name.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = filters.status === '' || dev.status === filters.status;
    const matchesRole = filters.role === '' || dev.role === filters.role;
    const matchesPosition = filters.position === '' || dev.position === filters.position;
    const matchesCountry = filters.country === '' || dev.country === filters.country;
    const matchesSkills = filters.skills.length === 0 || filters.skills.every(skillName => dev.skills.some(s => s.name === skillName));
    return matchesSearch && matchesStatus && matchesRole && matchesPosition && matchesCountry && matchesSkills;
  });
  // Pagination
  const indexOfLastDeveloper = currentPage * developersPerPage;
  const indexOfFirstDeveloper = indexOfLastDeveloper - developersPerPage;
  const currentDevelopers = filteredDevelopers.slice(indexOfFirstDeveloper, indexOfLastDeveloper);
  const totalPages = Math.ceil(filteredDevelopers.length / developersPerPage);
  const handleQuickFilter = (type: string, value: string) => {
    if (type === 'skill') {
      setFilters(prev => {
        if (prev.skills.includes(value)) {
          return {
            ...prev,
            skills: prev.skills.filter(s => s !== value)
          };
        } else {
          return {
            ...prev,
            skills: [...prev.skills, value]
          };
        }
      });
    } else if (type === 'status') {
      setFilters(prev => ({
        ...prev,
        status: prev.status === value ? '' : value
      }));
    } else if (type === 'role') {
      setFilters(prev => ({
        ...prev,
        role: prev.role === value ? '' : value
      }));
    }
  };
  const clearFilters = () => {
    setFilters({
      status: '',
      role: '',
      position: '',
      country: '',
      skills: []
    });
    setSearchTerm('');
    setCurrentPage(1);
  };
  const isFilterActive = (type: string, value: string) => {
    if (type === 'skill') return filters.skills.includes(value);
    if (type === 'status') return filters.status === value;
    if (type === 'role') return filters.role === value;
    return false;
  };
  return <div className="bg-[#F8FAFC] min-h-screen" data-id="element-640">
      {/* Editorial Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0F1115] via-[#1a1d24] to-[#111827] text-white overflow-hidden" data-id="element-641">
        {/* Subtle grain texture */}
        <div className="absolute inset-0 opacity-[0.015] bg-grain" data-id="element-642"></div>
        {/* PHP Code Background Image */}
        <div className="absolute inset-0 opacity-[0.15] overflow-hidden" data-id="element-643">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/koTEbxE6H3NVaj4P9TF4mh/Schermafbeelding_2025-10-11_150904.png" alt="PHP Code Background" className="w-full h-full object-cover object-center" data-id="element-644" />
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 relative z-10" data-id="element-645">
          <div className="max-w-4xl mx-auto text-center" data-id="element-646">
            <h1 className="font-display text-5xl lg:text-6xl font-bold mb-6 leading-tight tracking-tight" data-id="element-647">
              Verified developers you can hire with confidence.
            </h1>
            <p className="text-xl lg:text-2xl text-gray-300 mb-10 leading-relaxed" data-id="element-648">
              Search certified back- & front-end talent. Real badges, live
              availability, transparent rates.
            </p>
            {/* Primary & Secondary CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12" data-id="element-649">
              <a href="#browse" className="bg-[#d73b3e] text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-[#c02f32] transition-all duration-200 shadow-lg hover:shadow-xl" data-id="element-650">
                Browse certified talent
              </a>
              <Link to="/verification" className="bg-white bg-opacity-10 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-opacity-20 transition-all duration-200 border border-white border-opacity-20" data-id="element-651">
                How verification works
              </Link>
            </div>
            {/* Trust indicators */}
            <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-400" data-id="element-652">
              <div className="flex items-center gap-2" data-id="element-653">
                <CheckCircleIcon className="h-5 w-5 text-green-400" data-id="element-654" />
                <span data-id="element-655">3,500+ developers certified since 2018</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-656">
                <CheckCircleIcon className="h-5 w-5 text-green-400" data-id="element-657" />
                <span data-id="element-658">Avg. 7 days to shortlist</span>
              </div>
            </div>
          </div>
        </div>
        {/* Curved divider */}
        <div className="absolute bottom-0 left-0 right-0" data-id="element-659">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" data-id="element-660">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#F8FAFC" data-id="element-661" />
          </svg>
        </div>
      </section>
      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12" id="browse" data-id="element-662">
        {/* Discovery Modes & Quick Filters */}
        <div className="mb-8" data-id="element-663">
          {/* View Mode Toggles */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6" data-id="element-664">
            <div className="flex gap-2 bg-white rounded-full p-1 shadow-sm border border-gray-200" data-id="element-665">
              <button onClick={() => setViewMode('grid')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${viewMode === 'grid' ? 'bg-[#d73b3e] text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-666">
                <GridIcon className="h-4 w-4 inline mr-1" data-id="element-667" />
                Grid
              </button>
              <button onClick={() => setViewMode('spotlight')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${viewMode === 'spotlight' ? 'bg-[#d73b3e] text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-668">
                <StarIcon className="h-4 w-4 inline mr-1" data-id="element-669" />
                Spotlight
              </button>
              <button onClick={() => setViewMode('collections')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${viewMode === 'collections' ? 'bg-[#d73b3e] text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-670">
                <ListIcon className="h-4 w-4 inline mr-1" data-id="element-671" />
                Collections
              </button>
              <button onClick={() => setViewMode('map')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${viewMode === 'map' ? 'bg-[#d73b3e] text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-672">
                <MapIcon className="h-4 w-4 inline mr-1" data-id="element-673" />
                Map
              </button>
            </div>
            <button onClick={() => setIsFilterOpen(!isFilterOpen)} className="lg:hidden flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-200" data-id="element-674">
              <FilterIcon className="h-4 w-4" data-id="element-675" />
              Advanced filters
            </button>
          </div>
          {/* Quick Filter Chips */}
          <div className="flex flex-wrap gap-2 mb-6" data-id="element-676">
            {quickFilters.map(filter => <button key={filter.label} onClick={() => handleQuickFilter(filter.type, filter.value)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${isFilterActive(filter.type, filter.value) ? 'bg-[#d73b3e] text-white shadow-md' : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300 hover:shadow-sm'}`} data-id="element-677">
                {filter.label}
              </button>)}
            {(filters.skills.length > 0 || filters.status || filters.role) && <button onClick={clearFilters} className="px-4 py-2 rounded-full text-sm font-medium text-[#d73b3e] hover:bg-red-50 transition-colors duration-200" data-id="element-678">
                Clear all
              </button>}
          </div>
        </div>
        <div className="flex flex-col lg:flex-row gap-8" data-id="element-679">
          {/* Advanced Filters Sidebar */}
          <div className={`lg:w-1/4 ${isFilterOpen ? 'block' : 'hidden lg:block'}`} data-id="element-680">
            <div className="bg-white rounded-2xl p-6 sticky top-24 shadow-sm border border-gray-200" data-id="element-681">
              <div className="flex justify-between items-center mb-6" data-id="element-682">
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-683">
                  Narrow results
                </h2>
              </div>
              {/* Search */}
              <div className="mb-6" data-id="element-684">
                <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-685">
                  Search
                </label>
                <div className="relative" data-id="element-686">
                  <input type="text" id="search" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="block w-full rounded-xl border border-gray-300 px-4 py-3 pr-10 focus:border-[#d73b3e] focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:ring-opacity-20 sm:text-sm" placeholder="Name, title, or skill..." data-id="element-687" />
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3" data-id="element-688">
                    <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-689" />
                  </div>
                </div>
              </div>
              {/* Status Filter */}
              <div className="mb-6" data-id="element-690">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-691">
                  Status
                </label>
                <select value={filters.status} onChange={e => setFilters({
                ...filters,
                status: e.target.value
              })} className="block w-full rounded-xl border border-gray-300 px-3 py-3 focus:border-[#d73b3e] focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:ring-opacity-20 sm:text-sm" data-id="element-692">
                  <option value="" data-id="element-693">All Statuses</option>
                  {statuses.map(status => <option key={status} value={status} data-id="element-694">
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </option>)}
                </select>
              </div>
              {/* Role Filter */}
              <div className="mb-6" data-id="element-695">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-696">
                  Role
                </label>
                <select value={filters.role} onChange={e => setFilters({
                ...filters,
                role: e.target.value
              })} className="block w-full rounded-xl border border-gray-300 px-3 py-3 focus:border-[#d73b3e] focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:ring-opacity-20 sm:text-sm" data-id="element-697">
                  <option value="" data-id="element-698">All Roles</option>
                  {roles.map(role => <option key={role} value={role} data-id="element-699">
                      {role}
                    </option>)}
                </select>
              </div>
              {/* Position Filter */}
              <div className="mb-6" data-id="element-700">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-701">
                  Position
                </label>
                <select value={filters.position} onChange={e => setFilters({
                ...filters,
                position: e.target.value
              })} className="block w-full rounded-xl border border-gray-300 px-3 py-3 focus:border-[#d73b3e] focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:ring-opacity-20 sm:text-sm" data-id="element-702">
                  <option value="" data-id="element-703">All Positions</option>
                  {positions.map(position => <option key={position} value={position} data-id="element-704">
                      {position}
                    </option>)}
                </select>
              </div>
              {/* Country Filter */}
              <div className="mb-6" data-id="element-705">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-706">
                  Country
                </label>
                <select value={filters.country} onChange={e => setFilters({
                ...filters,
                country: e.target.value
              })} className="block w-full rounded-xl border border-gray-300 px-3 py-3 focus:border-[#d73b3e] focus:outline-none focus:ring-2 focus:ring-[#d73b3e] focus:ring-opacity-20 sm:text-sm" data-id="element-707">
                  <option value="" data-id="element-708">All Countries</option>
                  {countries.map(country => <option key={country} value={country} data-id="element-709">
                      {country}
                    </option>)}
                </select>
              </div>
            </div>
          </div>
          {/* Developers Grid */}
          <div className="lg:w-3/4" data-id="element-710">
            {/* Results count */}
            <div className="flex justify-between items-center mb-6" data-id="element-711">
              <p className="text-sm text-gray-600" data-id="element-712">
                Showing{' '}
                <span className="font-semibold text-gray-900" data-id="element-713">
                  {filteredDevelopers.length}
                </span>{' '}
                certified developers
              </p>
            </div>
            {filteredDevelopers.length === 0 ? <div className="bg-white rounded-2xl p-12 text-center border border-gray-200" data-id="element-714">
                <div className="mb-6" data-id="element-715">
                  <SearchIcon className="h-16 w-16 text-gray-300 mx-auto" data-id="element-716" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-717">
                  No exact matches
                </h3>
                <p className="text-gray-600 mb-6" data-id="element-718">
                  Try widening your rate or region filters
                </p>
                <div className="flex flex-wrap gap-2 justify-center" data-id="element-719">
                  <button onClick={() => setFilters({
                ...filters,
                country: ''
              })} className="px-4 py-2 rounded-full text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors duration-200" data-id="element-720">
                    EU
                  </button>
                  <button onClick={() => setFilters({
                ...filters,
                status: 'available'
              })} className="px-4 py-2 rounded-full text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors duration-200" data-id="element-721">
                    Remote
                  </button>
                  <button onClick={clearFilters} className="px-4 py-2 rounded-full text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors duration-200" data-id="element-722">
                    €50–€90/hr
                  </button>
                </div>
              </div> : <>
                {viewMode === 'grid' && <div className="grid md:grid-cols-2 gap-6" data-id="element-723">
                    {currentDevelopers.map(developer => <TalentCapsuleCard key={developer.id} developer={developer} data-id="element-724" />)}
                  </div>}
                {viewMode === 'spotlight' && <div className="space-y-6" data-id="element-725">
                    {/* Featured large card */}
                    {currentDevelopers.length > 0 && <TalentCapsuleCard developer={currentDevelopers[0]} featured data-id="element-726" />}
                    {/* Two smaller cards */}
                    <div className="grid md:grid-cols-2 gap-6" data-id="element-727">
                      {currentDevelopers.slice(1, 3).map(developer => <TalentCapsuleCard key={developer.id} developer={developer} data-id="element-728" />)}
                    </div>
                    {/* Rest in grid */}
                    <div className="grid md:grid-cols-2 gap-6" data-id="element-729">
                      {currentDevelopers.slice(3).map(developer => <TalentCapsuleCard key={developer.id} developer={developer} data-id="element-730" />)}
                    </div>
                  </div>}
                {viewMode === 'collections' && <div className="space-y-8" data-id="element-731">
                    <div data-id="element-732">
                      <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-733">
                        Top Laravel Agencies
                      </h3>
                      <div className="grid md:grid-cols-2 gap-6" data-id="element-734">
                        {currentDevelopers.slice(0, 4).map(developer => <TalentCapsuleCard key={developer.id} developer={developer} data-id="element-735" />)}
                      </div>
                    </div>
                    <div data-id="element-736">
                      <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-737">
                        New This Week
                      </h3>
                      <div className="grid md:grid-cols-2 gap-6" data-id="element-738">
                        {currentDevelopers.slice(4).map(developer => <TalentCapsuleCard key={developer.id} developer={developer} data-id="element-739" />)}
                      </div>
                    </div>
                  </div>}
                {viewMode === 'map' && <div className="bg-white rounded-2xl p-8 border border-gray-200 text-center" data-id="element-740">
                    <MapIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" data-id="element-741" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-742">
                      Map view coming soon
                    </h3>
                    <p className="text-gray-600" data-id="element-743">
                      Visualize developers by location on an interactive map
                    </p>
                  </div>}
              </>}
            {/* Pagination */}
            {filteredDevelopers.length > 0 && viewMode === 'grid' && <div className="mt-8 flex justify-center" data-id="element-744">
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-745" />
              </div>}
          </div>
        </div>
      </div>
      {/* Certification Exams Section */}
      <section id="certifications" className="py-20 bg-gradient-to-br from-gray-50 to-white" data-id="element-746">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-747">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-748">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-749">
              Get Certified
            </h2>
            <p className="text-lg text-gray-600" data-id="element-750">
              Validate your expertise with industry-recognized certifications
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-751">
            {certificationExams.map(exam => <div key={exam.id} className="bg-white rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden group" data-id="element-752">
                <div className="p-8" data-id="element-753">
                  {/* Badge */}
                  <div className="flex justify-center mb-6" data-id="element-754">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#d73b3e] to-[#c02f32] rounded-full flex items-center justify-center shadow-lg p-3" data-id="element-755">
                      <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/6raD3sQbYhrAYJrpu7Fmsg/Laravel.jpg" alt="Laravel" className="w-full h-full object-contain" data-id="element-756" />
                    </div>
                  </div>
                  {/* Content */}
                  <div className="text-center" data-id="element-757">
                    <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-758">
                      {exam.name}
                    </h3>
                    <p className="text-gray-600 mb-6 text-sm leading-relaxed" data-id="element-759">
                      {exam.description}
                    </p>
                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 rounded-xl" data-id="element-760">
                      <div data-id="element-761">
                        <div className="text-2xl font-bold text-[#d73b3e]" data-id="element-762">
                          {exam.questions}
                        </div>
                        <div className="text-xs text-gray-600 mt-1" data-id="element-763">
                          Questions
                        </div>
                      </div>
                      <div data-id="element-764">
                        <div className="text-2xl font-bold text-[#d73b3e]" data-id="element-765">
                          {exam.duration}
                        </div>
                        <div className="text-xs text-gray-600 mt-1" data-id="element-766">
                          Minutes
                        </div>
                      </div>
                    </div>
                    {/* Topics */}
                    <div className="mb-6" data-id="element-767">
                      <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-768">
                        Topics Covered:
                      </h4>
                      <div className="flex flex-wrap gap-2 justify-center" data-id="element-769">
                        {exam.topics.slice(0, 3).map((topic, index) => <span key={index} className="text-xs bg-gray-100 text-gray-700 px-3 py-1.5 rounded-full font-medium" data-id="element-770">
                            {topic}
                          </span>)}
                        {exam.topics.length > 3 && <span className="text-xs bg-gray-100 text-gray-700 px-3 py-1.5 rounded-full font-medium" data-id="element-771">
                            +{exam.topics.length - 3} more
                          </span>}
                      </div>
                    </div>
                    {/* CTA Button */}
                    <button className="w-full px-6 py-3 bg-[#d73b3e] text-white rounded-full font-semibold hover:bg-[#c02f32] transition-all duration-200 shadow-sm group-hover:shadow-md" data-id="element-772">
                      Start Certification
                    </button>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>
    </div>;
};
export default Developers;